import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';
import { waLink } from '../lib/wa.js';
import { SearchableSelect } from '../components/primitives/SearchableSelect.js';

interface Cert { status: string; }
interface Worker {
  id: string;
  name: string;
  role: string;
  roles?: string[];
  status: string;
  postcode: string;
  phone: string;
  photo?: string | null;
  companyRef?: string | null;
  certs?: Cert[];
}
interface Project {
  id: string;
  code: string;
  name: string;
  status: string;
  supervisorId?: string | null;
  clientPMId?: string | null;
  clsMgrId?: string | null;
}
interface Contact {
  id: string;
  name: string;
  role: string | null;
  customer: string | null;
  phone: string | null;
  email?: string | null;
}
interface DnaRecord { id: string; workerId: string; }

function stBdg(status: string): JSX.Element {
  return <span className={`badge b-${status}`}>{status}</span>;
}

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

export function BulkDeploy() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [fRole, setFRole] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [project, setProject] = useState('');
  const [startDate, setStartDate] = useState<string>(todayISO());
  const [startTime, setStartTime] = useState('07:30');
  const [duration, setDuration] = useState('Until project end');
  const [supervisor, setSupervisor] = useState('');
  const [requester, setRequester] = useState('');
  const [meet, setMeet] = useState('');
  const [notes, setNotes] = useState('');
  const [notifyWA, setNotifyWA] = useState(true);
  const [notifySMS, setNotifySMS] = useState(false);

  const { data: workers = [] } = useQuery({ queryKey: ['workers'], queryFn: () => api.get<Worker[]>('/workers').then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ['projects'], queryFn: () => api.get<Project[]>('/projects').then((r) => r.data) });
  const { data: contacts = [] } = useQuery({ queryKey: ['contacts'], queryFn: () => api.get<Contact[]>('/contacts').then((r) => r.data) });
  const { data: dnaRecords = [] } = useQuery({ queryKey: ['dna-records'], queryFn: () => api.get<DnaRecord[]>('/dna-records').then((r) => r.data).catch(() => [] as DnaRecord[]) });

  const dnaCounts = useMemo(() => {
    const m = new Map<string, number>();
    dnaRecords.forEach((d) => m.set(d.workerId, (m.get(d.workerId) ?? 0) + 1));
    return m;
  }, [dnaRecords]);

  const eligible = workers.filter((w) => w.status !== 'unavailable' && w.status !== 'dnu');
  const roleOpts = useMemo(() => {
    const all = new Set<string>();
    workers.forEach((w) => { if (w.role) all.add(w.role); (w.roles || []).forEach((r) => all.add(r)); });
    return [...all].sort();
  }, [workers]);

  const filtered = useMemo(() => {
    let r = [...eligible];
    const s = search.toLowerCase().trim();
    if (s) r = r.filter((w) => [w.name, w.role, w.companyRef ?? ''].join(' ').toLowerCase().includes(s));
    if (fRole) r = r.filter((w) => w.role === fRole || (w.roles || []).includes(fRole));
    return r;
  }, [eligible, search, fRole]);

  const toggle = (id: string) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const selectAll = (on: boolean) => {
    setSelected((s) => {
      const n = new Set(s);
      filtered.forEach((w) => { if (on) n.add(w.id); else n.delete(w.id); });
      return n;
    });
  };

  const liveProjects = projects.filter((p) => p.status === 'live');
  const selectedProject = projects.find((p) => p.id === project);

  useEffect(() => {
    if (!project) { setSupervisor(''); setRequester(''); return; }
    const p = projects.find((x) => x.id === project);
    if (!p) return;
    setSupervisor(p.supervisorId || p.clientPMId || p.clsMgrId || '');
    setRequester(p.clientPMId || p.clsMgrId || p.supervisorId || '');
  }, [project, projects]);

  const supervisorContact = contacts.find((c) => c.id === supervisor);
  const requesterContact = contacts.find((c) => c.id === requester);

  const dispatch = useMutation({
    mutationFn: async () => {
      const ids = [...selected];
      const meetText = meet.trim() || 'Report to site office';
      const results = await Promise.allSettled(
        ids.map((workerId) => api.post('/deployments', {
          workerId,
          projectId: project,
          startDate,
          startTime,
          duration,
          supervisorId: supervisor || null,
          meet: meetText,
          requesterId: requester || null,
          notes: notes.trim() || null,
          waMessageSent: notifyWA,
        }))
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const fail = results.length - ok;

      const proj = selectedProject;
      ids.forEach((wid, idx) => {
        const w = workers.find((x) => x.id === wid);
        if (!w?.phone) return;
        const first = w.name.split(' ')[0] || w.name;
        const lines = [
          `Hi ${first}, you have been deployed to ${proj?.code ?? project} — ${proj?.name ?? ''}.`,
          ...(proj && (proj as any).address ? [`Address: ${(proj as any).address}`] : []),
          `Start: ${startDate} at ${startTime}`,
          ...(meetText ? [`Meet: ${meetText}`] : []),
          ...(notes.trim() ? [`Notes: ${notes.trim()}`] : []),
          ...(supervisorContact ? [`Supervisor: ${supervisorContact.name}${supervisorContact.phone ? ' · ' + supervisorContact.phone : ''}`] : []),
          `— CLS Construction Logistics Services`,
        ];
        const msg = lines.join('\n');
        if (notifyWA) setTimeout(() => window.open(waLink(w.phone, msg), '_blank'), idx * 250);
        if (notifySMS) setTimeout(() => window.open(`sms:${w.phone}?body=${encodeURIComponent(msg)}`, '_blank'), idx * 250 + 100);
      });
      return { ok, fail };
    },
    onSuccess: ({ ok, fail }) => {
      qc.invalidateQueries({ queryKey: ['deployments'] });
      qc.invalidateQueries({ queryKey: ['workers'] });
      qc.invalidateQueries({ queryKey: ['dashboard', 'overview'] });
      if (fail === 0) {
        toast.success('🚀 Deployed', `${ok} worker${ok !== 1 ? 's' : ''} dispatched to ${selectedProject?.code ?? project}`);
        setSelected(new Set());
        setNotes('');
        setMeet('');
      } else {
        toast.warn('Partial dispatch', `${ok} ok · ${fail} failed`);
      }
    },
    onError: (e: any) => toast.error('Dispatch failed', e?.response?.data?.message ?? 'Check fields and try again'),
  });

  const canDispatch = selected.size > 0 && project && startDate && !dispatch.isPending;

  const projectOptions = useMemo(() => liveProjects.map((p) => ({ value: p.id, label: `${p.code} — ${p.name}` })), [liveProjects]);
  const supervisorOptions = useMemo(() => [
    { value: '', label: '— Select supervisor —' },
    ...contacts.map((c) => ({
      value: c.id,
      label: `${c.name}${c.customer ? ' (' + c.customer + ')' : ''}${c.role ? ' — ' + c.role : ''}`,
    })),
  ], [contacts]);
  const requesterOptions = useMemo(() => [
    { value: '', label: '— No requester —' },
    ...contacts.map((c) => ({
      value: c.id,
      label: `${c.name}${c.customer ? ' (' + c.customer + ')' : ''}${c.role ? ' — ' + c.role : ''}`,
    })),
  ], [contacts]);

  return (
    <div className="page" id="page-bulkdeploy">
      <div className="hbar">
        <div>
          <div className="ph">🚀 Bulk Deployment Wizard</div>
          <div className="ps">Select workers and deploy to a project in one action</div>
        </div>
        {selected.size > 0 ? (
          <button
            className="btn btn-primary"
            id="bulk-dispatch-btn"
            onClick={() => dispatch.mutate()}
            disabled={!canDispatch}
          >
            {dispatch.isPending ? 'Dispatching…' : '🚀 Dispatch Selected Workers'}
          </button>
        ) : null}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 14, minHeight: 500 }}>
        <div className="card" style={{ overflowY: 'auto' }}>
          <div className="sec-title" style={{ marginBottom: 10 }}>Available Workers — select to deploy</div>
          <div style={{ display: 'flex', gap: 7, marginBottom: 10, flexWrap: 'wrap' }}>
            <div className="tbl-search" style={{ flex: 1, minWidth: 150 }}>
              <span style={{ color: 'var(--faint)' }}>⌕</span>
              <input id="bulk-search" placeholder="Search workers…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <select className="form-select" id="bulk-role-filter" value={fRole} onChange={(e) => setFRole(e.target.value)} style={{ fontSize: 11, padding: '5px 8px' }}>
              <option value="">All Roles</option>
              {roleOpts.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
            <button className="btn btn-ghost btn-xs" onClick={() => selectAll(true)}>Select All</button>
            <button className="btn btn-ghost btn-xs" onClick={() => selectAll(false)}>Clear</button>
          </div>
          <div id="bulk-workers-list">
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 30, color: 'var(--muted)' }}>No available workers match</div>
            ) : filtered.map((w) => {
              const checked = selected.has(w.id);
              const certs = (w.certs ?? []).filter((c) => c.status === 'valid').length;
              const expired = (w.certs ?? []).filter((c) => c.status === 'expired').length;
              const initials = w.name.split(' ').map((n) => n[0] ?? '').join('').slice(0, 2).toUpperCase();
              const dna = dnaCounts.get(w.id) ?? 0;
              const border = checked ? 'var(--accent)' : 'var(--border)';
              const bg = checked ? 'var(--accent-dim)' : 'var(--card)';
              return (
                <div
                  key={w.id}
                  onClick={() => toggle(w.id)}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: `2px solid ${border}`, marginBottom: 7, cursor: 'pointer', background: bg, transition: 'all .15s' }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(w.id)}
                    onClick={(e) => e.stopPropagation()}
                    style={{ accentColor: 'var(--accent)', width: 16, height: 16, flexShrink: 0 }}
                  />
                  {w.photo ? (
                    <img src={w.photo} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 13, color: '#000', flexShrink: 0 }}>{initials}</div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{w.name}</div>
                    <div style={{ fontSize: 10, color: 'var(--muted)' }}>{w.role} · {w.companyRef || `ID:${w.id}`}</div>
                    <div style={{ fontSize: 9, marginTop: 2 }}>
                      {stBdg(w.status)}{' '}
                      <span style={{ color: 'var(--live)' }}>{certs} valid</span>
                      {expired > 0 ? <span style={{ color: 'var(--danger)', fontSize: 9 }}> ⚠{expired} expired</span> : null}
                      {dna > 0 ? <span title={`${dna} DNA on record`} style={{ background: 'rgba(239,68,68,.15)', color: '#ef4444', fontSize: 9, padding: '1px 6px', borderRadius: 4, fontWeight: 700, marginLeft: 4 }}>✗{dna} DNA</span> : null}
                    </div>
                  </div>
                  {checked ? <div style={{ color: 'var(--accent)', fontSize: 18, fontWeight: 800, flexShrink: 0 }}>✓</div> : null}
                </div>
              );
            })}
          </div>
        </div>
        <div className="card" style={{ overflowY: 'auto' }}>
          <div className="sec-title" style={{ marginBottom: 12 }}>Deployment Details</div>
          <div className="form-row">
            <label className="form-label">Project</label>
            <SearchableSelect
              id="bulk-project"
              value={project}
              onChange={setProject}
              placeholder="— Select project —"
              options={[{ value: '', label: '— Select project —' }, ...projectOptions]}
            />
          </div>
          <div className="fg2">
            <div className="form-row"><label className="form-label">Start Date</label><input type="date" className="form-input" id="bulk-start-date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
            <div className="form-row"><label className="form-label">Start Time</label><input type="time" className="form-input" id="bulk-start-time" value={startTime} onChange={(e) => setStartTime(e.target.value)} /></div>
          </div>
          <div className="form-row">
            <label className="form-label">Duration</label>
            <select className="form-select" id="bulk-duration" value={duration} onChange={(e) => setDuration(e.target.value)}>
              <option>Until project end</option>
              <option>1 week</option>
              <option>2 weeks</option>
              <option>1 month</option>
            </select>
          </div>
          <div className="form-row">
            <label className="form-label">Supervisor</label>
            <SearchableSelect
              id="bulk-supervisor"
              value={supervisor}
              onChange={setSupervisor}
              placeholder="— Select supervisor —"
              options={supervisorOptions}
            />
          </div>
          <div className="form-row">
            <label className="form-label">Meeting Point</label>
            <input type="text" className="form-input" id="bulk-meet" placeholder="Where to report to" value={meet} onChange={(e) => setMeet(e.target.value)} />
          </div>
          <div className="form-row">
            <label className="form-label">
              Requested By <span style={{ fontSize: 10, color: 'var(--muted)', fontWeight: 400 }}>— client contact</span>
            </label>
            <SearchableSelect
              id="bulk-requester"
              value={requester}
              onChange={setRequester}
              placeholder="— No requester —"
              options={requesterOptions}
            />
          </div>
          <div className="form-row">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" id="bulk-notes" style={{ minHeight: 60 }} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, marginBottom: 10 }}>
            <div className="sec-title" style={{ marginBottom: 6 }}>Notify via</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                <input type="checkbox" id="bulk-notify-wa" checked={notifyWA} onChange={(e) => setNotifyWA(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
                <span style={{ color: '#25D366', fontWeight: 600 }}>WhatsApp</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, cursor: 'pointer' }}>
                <input type="checkbox" id="bulk-notify-sms" checked={notifySMS} onChange={(e) => setNotifySMS(e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
                <span style={{ color: 'var(--sms)', fontWeight: 600 }}>SMS</span>
              </label>
            </div>
          </div>
          <div id="bulk-selected-summary" style={{ marginBottom: 10, fontSize: 11 }}>
            {selected.size > 0 ? (
              <>
                <strong style={{ color: 'var(--accent)' }}>{selected.size} worker{selected.size !== 1 ? 's' : ''} selected</strong>
                <span style={{ color: 'var(--muted)' }}> — ready to deploy</span>
              </>
            ) : (
              <span style={{ color: 'var(--muted)' }}>Select workers from the list on the left</span>
            )}
          </div>
          <button
            className="btn btn-primary"
            style={{ width: '100%', justifyContent: 'center', padding: 12 }}
            onClick={() => dispatch.mutate()}
            disabled={!canDispatch}
          >
            {dispatch.isPending ? 'Dispatching…' : '🚀 Deploy Selected Workers'}
          </button>
        </div>
      </div>
    </div>
  );
}
